import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, Sparkles, Upload } from "lucide-react";
import { api, BACKEND_URL, getToken } from "@/lib/api";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, AInput, ATextarea, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";
import { toast } from "sonner";

const empty = {
  name: "", category: "dining", subcategory: "", price: 0, original_price: "", description: "",
  images: [], material: "", dimensions: "", stock: 100, issued_stock: 0, featured: false, collection: "",
  gift_persons: [], gift_occasions: [], variants: [],
};

export default function AdminProductEditor() {
  const { c } = useAdminTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState(empty);
  const [meta, setMeta] = useState({ categories: [], collections: [], persons: [], occasions: [] });
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    Promise.all([api("/categories"), api("/collections"), api("/gift-persons"), api("/gift-occasions")])
      .then(([categories, collections, persons, occasions]) => setMeta({ categories, collections, persons, occasions }));
  }, []);

  const load = useCallback(() => {
    if (!isEdit) return;
    api(`/admin/products/${id}`).then((p) => {
      setForm({ ...empty, ...p, original_price: p.original_price ?? "", images: p.images || [], gift_persons: p.gift_persons || [], gift_occasions: p.gift_occasions || [], variants: p.variants || [] });
    }).finally(() => setLoading(false));
  }, [id, isEdit]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const subcats = meta.categories.find((cat) => cat.id === form.category)?.subcategories || [];

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${BACKEND_URL}/api/admin/upload`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        uploaded.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...f.images.filter(Boolean), ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };
  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter(Boolean).filter((_, j) => j !== i) }));
  const moveImage = (i, dir) => setForm((f) => {
    const arr = f.images.filter(Boolean);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, images: arr };
  });

  const aiGenerate = async () => {
    const kw = window.prompt("Describe the product in a few keywords (colour, material, item):", form.name || "");
    if (!kw) return;
    setAiBusy(true);
    try {
      const r = await api("/admin/ai/product-copy", { method: "POST", body: { keywords: kw, category: form.category } });
      setForm((f) => ({ ...f, name: r.name || f.name, description: r.description || f.description, material: r.material || f.material, dimensions: r.dimensions || f.dimensions }));
      toast.success("AI copy generated");
    } catch (e) { toast.error(e.message); } finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast("Name is required");
    setBusy(true);
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      original_price: form.original_price === "" ? null : Number(form.original_price),
      stock: Number(form.stock) || 0,
      issued_stock: Number(form.issued_stock) || 0,
      images: form.images.filter(Boolean),
      variants: form.variants.map((v) => ({ ...v, stock: Number(v.stock) || 0, price_delta: Number(v.price_delta) || 0 })),
    };
    try {
      if (isEdit) { await api(`/products/${id}`, { method: "PUT", body: payload }); toast.success("Product updated"); }
      else { await api("/products", { method: "POST", body: payload }); toast.success("Product created"); }
      navigate("/admin/products");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (loading) return <Spinner full />;

  return (
    <div className="fade-up" style={{ maxWidth: 820 }}>
      <AdminPageTitle title={isEdit ? "Edit Product" : "New Product"}
        action={<AButton variant="outline" onClick={() => navigate("/admin/products")}>Cancel</AButton>} />

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <ACard>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <AButton variant="soft" data-testid="ai-generate-copy" onClick={aiGenerate} disabled={aiBusy}>
              <Sparkles size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{aiBusy ? "Writing…" : "Generate with AI"}
            </AButton>
          </div>
          <div style={{ display: "grid", gap: 14 }}>
            <AInput label="Name" data-testid="pe-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Select c={c} label="Category" value={form.category} onChange={(v) => { set("category", v); set("subcategory", ""); }} options={meta.categories.map((x) => ({ v: x.id, l: x.name }))} testid="pe-category" />
              <Select c={c} label="Subcategory" value={form.subcategory} onChange={(v) => set("subcategory", v)} options={[{ v: "", l: "—" }, ...subcats.map((x) => ({ v: x.id, l: x.name }))]} testid="pe-subcategory" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14 }}>
              <AInput label="Price (₹)" type="number" data-testid="pe-price" value={form.price} onChange={(e) => set("price", e.target.value)} />
              <AInput label="Original Price" type="number" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} />
              <AInput label="Stock (remaining)" type="number" data-testid="pe-stock" value={form.stock} onChange={(e) => set("stock", e.target.value)} />
              <AInput label="Issued stock" type="number" data-testid="pe-issued" value={form.issued_stock} onChange={(e) => set("issued_stock", e.target.value)} />
            </div>
            <p style={{ fontSize: 12, color: "var(--muted,#8E8578)", margin: "-4px 0 0" }}>Issued stock is the initial batch baseline. "Few Stocks Left" shows when remaining ≤ 10% of it. Leave 0 to default to current stock.</p>
            <ATextarea label="Description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <AInput label="Material" value={form.material} onChange={(e) => set("material", e.target.value)} />
              <AInput label="Dimensions" value={form.dimensions} onChange={(e) => set("dimensions", e.target.value)} />
            </div>
            <Select c={c} label="Collection" value={form.collection} onChange={(v) => set("collection", v)} options={[{ v: "", l: "—" }, ...meta.collections.map((x) => ({ v: x.id, l: x.name }))]} />
          </div>
        </ACard>

        <ACard>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <Label c={c} style={{ margin: 0 }}>Product Images</Label>
            <label data-testid="pe-upload-label" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: c.primarySoft, color: c.primarySoftText, borderRadius: 10, padding: "9px 15px", fontWeight: 700, fontSize: 13, cursor: uploading ? "wait" : "pointer" }}>
              <Upload size={14} />{uploading ? "Uploading…" : "Upload from device"}
              <input data-testid="pe-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} disabled={uploading} onChange={onFiles} />
            </label>
          </div>
          <p style={{ fontSize: 12, color: c.mutedText, margin: "0 0 12px" }}>JPG, PNG or WebP · up to 5MB each. First image is the primary. Drag order with the arrows.</p>
          {form.images.filter(Boolean).length > 0 && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              {form.images.filter(Boolean).map((im, i) => (
                <div key={i} data-testid={`pe-image-${i}`} style={{ position: "relative", width: 92, borderRadius: 10, overflow: "hidden", border: `1px solid ${i === 0 ? c.primary : c.border}` }}>
                  <img src={im} alt="" style={{ width: 92, height: 92, objectFit: "cover", display: "block" }} />
                  {i === 0 && <span style={{ position: "absolute", top: 4, left: 4, background: c.primary, color: c.onPrimary, fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 999 }}>PRIMARY</span>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 4px", background: c.surfaceAlt }}>
                    <button type="button" title="Move left" onClick={() => moveImage(i, -1)} disabled={i === 0} style={imgBtn(c)}>&#8592;</button>
                    <button type="button" title="Remove" data-testid={`pe-image-remove-${i}`} onClick={() => removeImage(i)} style={{ ...imgBtn(c), color: c.danger }}><Trash2 size={13} /></button>
                    <button type="button" title="Move right" onClick={() => moveImage(i, 1)} disabled={i === form.images.filter(Boolean).length - 1} style={imgBtn(c)}>&#8594;</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <Label c={c}>Or paste image URLs (fallback)</Label>
          <ATextarea data-testid="pe-images" rows={2} placeholder="One image URL per line" value={form.images.join("\n")} onChange={(e) => set("images", e.target.value.split("\n").map((s) => s.trim()))} />
        </ACard>

        <ACard>
          <Label c={c}>Featured</Label>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input data-testid="pe-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 14 }}>Show in Featured Pieces on the storefront home</span>
          </label>

          <Label c={c} style={{ marginTop: 18 }}>Gift · For Person</Label>
          <Chips c={c} items={meta.persons} selected={form.gift_persons} onToggle={(v) => toggleArr("gift_persons", v)} />
          <Label c={c} style={{ marginTop: 14 }}>Gift · Occasion</Label>
          <Chips c={c} items={meta.occasions} selected={form.gift_occasions} onToggle={(v) => toggleArr("gift_occasions", v)} />
        </ACard>

        <ACard>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Label c={c} style={{ margin: 0 }}>Variants (optional)</Label>
            <AButton variant="soft" onClick={() => set("variants", [...form.variants, { color: "", size: "", sku: "", stock: 0, price_delta: 0 }])}><Plus size={14} /> Add</AButton>
          </div>
          {form.variants.length === 0 && <p style={{ color: c.mutedText, fontSize: 13, margin: 0 }}>No variants. Base stock is used.</p>}
          {form.variants.map((v, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 0.8fr 0.9fr auto", gap: 8, marginBottom: 8, alignItems: "center" }}>
              <AInput placeholder="Color" value={v.color} onChange={(e) => updateVariant(i, "color", e.target.value)} />
              <AInput placeholder="Size" value={v.size} onChange={(e) => updateVariant(i, "size", e.target.value)} />
              <AInput placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(i, "sku", e.target.value)} />
              <AInput placeholder="Stock" type="number" value={v.stock} onChange={(e) => updateVariant(i, "stock", e.target.value)} />
              <AInput placeholder="±Price" type="number" value={v.price_delta} onChange={(e) => updateVariant(i, "price_delta", e.target.value)} />
              <AButton variant="ghost" style={{ padding: 8, color: c.danger }} onClick={() => set("variants", form.variants.filter((_, j) => j !== i))}><Trash2 size={15} /></AButton>
            </div>
          ))}
        </ACard>

        <div style={{ display: "flex", gap: 12 }}>
          <AButton data-testid="pe-save" onClick={save} disabled={busy} style={{ flex: 1 }}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}</AButton>
        </div>
      </div>
    </div>
  );

  function updateVariant(i, k, val) {
    set("variants", form.variants.map((v, j) => (j === i ? { ...v, [k]: val } : v)));
  }
}

function Label({ c, children, style }) {
  return <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: c.mutedText, marginBottom: 8, fontWeight: 700, ...style }}>{children}</div>;
}
function imgBtn(c) {
  return { background: "none", border: "none", cursor: "pointer", color: c.onSurfaceSecondary, padding: "2px 5px", fontSize: 13, display: "flex", alignItems: "center" };
}
function Select({ c, label, value, onChange, options, testid }) {
  return (
    <label style={{ display: "block" }}>
      {label && <Label c={c}>{label}</Label>}
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: "11px 14px", color: c.onSurface, fontSize: 14, outline: "none", fontFamily: "DM Sans" }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
function Chips({ c, items, selected, onToggle }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map((it) => {
        const on = selected.includes(it.id);
        return (
          <button key={it.id} onClick={() => onToggle(it.id)} style={{ padding: "7px 13px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 600,
            border: `1px solid ${on ? c.primary : c.border}`, background: on ? c.primarySoft : c.surface, color: on ? c.primarySoftText : c.onSurfaceSecondary }}>
            {it.name}
          </button>
        );
      })}
    </div>
  );
}
